import { Injectable } from '@angular/core';
import * as moment from 'moment';
import { DatabaseService } from './database.service';

export type SyncStateStatus = 'pending' | 'running' | 'done' | 'error';

export interface SyncStateRecord {
    resource: string;
    status: SyncStateStatus;
    cursor: string | null;
    page: number;
    retry_count: number;
    last_status: number | null;
    last_error: string | null;
    started_at: string | null;
    last_attempt_at: string | null;
    last_success_at: string | null;
    next_retry_at: string | null;
    updated_at: string;
}

@Injectable({
    providedIn: 'root'
})
export class SyncStateDBService extends DatabaseService {
    override classname: string = 'SyncStateDBService';
    override table = 'sync_state';
    override keyPath = 'resource';
    override typeKey = 'TEXT';
    protected override useUuidKey = false;

    override indices = [
        {name: 'by_status', field: 'status', unique: false, multiEntry: false},
        {name: 'by_next_retry_at', field: 'next_retry_at', unique: false, multiEntry: false},
    ];

    private now(): string{
        return (moment as any)().format('YYYY-MM-DD HH:mm:ss');
    }

    private createDefault(resource: string, cursor: string | null = null): SyncStateRecord{
        return {
            resource,
            status: 'pending',
            cursor,
            page: 1,
            retry_count: 0,
            last_status: null,
            last_error: null,
            started_at: null,
            last_attempt_at: null,
            last_success_at: null,
            next_retry_at: null,
            updated_at: this.now(),
        };
    }

    async getState(resource: string, fallbackCursor: string | null = null): Promise<SyncStateRecord>{
        try{
            const state = await this.getByKey(resource) as SyncStateRecord;
            return {
                ...this.createDefault(resource, fallbackCursor),
                ...state,
                page: state.page || 1,
                retry_count: state.retry_count || 0,
            };
        }catch(_){
            const state = this.createDefault(resource, fallbackCursor);
            await this.put(state);
            return state;
        }
    }

    async markRunning(resource: string, cursor: string | null, page: number = 1): Promise<SyncStateRecord>{
        const current = await this.getState(resource, cursor);
        const state: SyncStateRecord = {
            ...current,
            status: 'running',
            cursor,
            page: page || current.page || 1,
            started_at: current.started_at || this.now(),
            last_attempt_at: this.now(),
            last_error: null,
            last_status: null,
            next_retry_at: null,
            updated_at: this.now(),
        };

        return this.put(state);
    }

    async markPageDone(resource: string, nextPage: number): Promise<SyncStateRecord>{
        const current = await this.getState(resource);
        const state: SyncStateRecord = {
            ...current,
            status: 'running',
            page: nextPage,
            last_attempt_at: this.now(),
            updated_at: this.now(),
        };

        return this.put(state);
    }

    async markDone(resource: string, cursor: string): Promise<SyncStateRecord>{
        const current = await this.getState(resource);
        const state: SyncStateRecord = {
            ...current,
            status: 'done',
            cursor,
            page: 1,
            retry_count: 0,
            last_status: null,
            last_error: null,
            started_at: null,
            last_attempt_at: this.now(),
            last_success_at: this.now(),
            next_retry_at: null,
            updated_at: this.now(),
        };

        return this.put(state);
    }

    async markError(resource: string, error: any, retryDelaySeconds: number = 60): Promise<SyncStateRecord>{
        const current = await this.getState(resource);
        const retryCount = (current.retry_count || 0) + 1;
        const state: SyncStateRecord = {
            ...current,
            status: 'error',
            retry_count: retryCount,
            last_status: error?.status || null,
            last_error: this.formatError(error),
            last_attempt_at: this.now(),
            next_retry_at: (moment as any)().add(retryDelaySeconds, 'seconds').format('YYYY-MM-DD HH:mm:ss'),
            updated_at: this.now(),
        };

        return this.put(state);
    }

    async resetResources(resources: string[]): Promise<void>{
        if(!this.initialized){
            await this.createTable();
        }

        for(const resource of resources){
            await this.del(resource).catch(() => null);
        }
    }

    private formatError(error: any): string{
        if(!error){
            return 'Erro desconhecido';
        }

        if(typeof error === 'string'){
            return error;
        }

        if(error?.message){
            return error.message;
        }

        if(error?.statusText){
            return error.statusText;
        }

        try{
            return JSON.stringify(error);
        }catch(_){
            return 'Erro não serializável';
        }
    }
}
